/**
 * EditForm component for modifying an existing dataset.
 */

import React, { useState, useEffect, useRef } from "react";
import PopupWrapper from "../../../../common/pyramid/PopupWrapper";
import { Form, SimpleItem, GroupItem, Label, ButtonItem } from "devextreme-react/form";
import Button from "devextreme-react/button";
import { useAuth } from "providers/AuthProvider";
import useItemOwner from "hooks/useItemOwner";
import { horusApi } from "services/api/horus";
import { HORUS_DATASET_TYPES } from "data/ENUM/add-options";

/**
 * Default popup configuration for the EditForm component
 */
export const popupConfigs = {
  width: "auto",
  height: "auto",
  title: "Edit Dataset",
  icon: "edit",
};

/**
 * EditForm component allows users to view dataset details.
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.isVisible - Controls the visibility of the popup
 * @param {Function} props.onCancel - Callback function when the operation is cancelled
 * @param {Function} props.onConfirm - Callback function when changes are confirmed
 * @param {Object} props.dataset - The dataset object to be edited
 */
const EditForm = ({ isVisible, onCancel, onConfirm, dataset }) => {
  const { companyList, userList } = useAuth();
  const api = horusApi();

  const [formData, setFormData] = useState(dataset || {});
  const [isDownloading, setIsDownloading] = useState(false);
  const formRef = useRef(null);

  const ownedItem = useItemOwner(dataset, "group");

  useEffect(() => {
    if (dataset) {
      setFormData({ ...dataset });
    }
  }, [dataset]);

  const handleDownloadInputs = async (datasetType) => {
    if (!datasetType || !dataset?.id) return;
    setIsDownloading(true);
    try {
      await api.downloadInputFiles({
        id: dataset.id,
        datasetType,
      });
    } catch (error) {
      console.error("Error downloading input files:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const updatedData = {
        ...dataset,
        description: formData.description,
      };
      await onConfirm(updatedData);
    } catch (error) {
      console.error("Error updating dataset:", error);
    }
  };

  return (
    <PopupWrapper
      {...popupConfigs}
      isVisible={isVisible}
      onCancel={onCancel}
      onConfirm={handleSubmit}
      isConfirmDisabled={!ownedItem}
    >
      <Form
        ref={formRef}
        formData={formData}
        onFieldDataChanged={(e) =>
          setFormData({ ...formData, [e.dataField]: e.value })
        }
      >
        {/* Dataset Information Section */}
        <GroupItem caption="Informations" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="description"
            editorType="dxTextBox"
            isRequired={true}
            editorOptions={{
              readOnly: !ownedItem,
            }}
          >
            <Label text="Description" />
          </SimpleItem>

          <SimpleItem
            dataField="evaluationDate"
            editorType="dxDateBox"
            editorOptions={{
              displayFormat: "yyyy-MM-dd",
              readOnly: true,
            }}
            isRequired={true}
          >
            <Label text="Evaluation Date" />
          </SimpleItem>
        </GroupItem>

        {/* Audit Information Section */}
        <GroupItem caption="Audit" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="createdAt"
            editorType="dxDateBox"
            editorOptions={{
              displayFormat: "yyyy-MM-dd",
              readOnly: true,
            }}
            isRequired={true}
          >
            <Label text="Creation Date" />
          </SimpleItem>

          <SimpleItem
            dataField="groupId"
            editorType="dxSelectBox"
            editorOptions={{
              readOnly: true,
              dataSource: companyList,
              displayExpr: "displayName",
              valueExpr: "id",
            }}
          >
            <Label text="Created by company" />
          </SimpleItem>

          <SimpleItem
            dataField="userId"
            editorType="dxSelectBox"
            editorOptions={{
              readOnly: true,
              dataSource: userList,
              displayExpr: "displayName",
              valueExpr: "id",
            }}
          >
            <Label text="Created by user" />
          </SimpleItem>
        </GroupItem>

        <GroupItem caption="Download inputs" colCount={4}>
          <SimpleItem
            colSpan={4}
            render={() => (
              <div>
                <div style={{ marginBottom: 8 }}>Select an input type:</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {HORUS_DATASET_TYPES.map((type) => (
                    <Button
                      key={type.id}
                      text={type.label}
                      icon="download"
                      type="default"
                      stylingMode="contained"
                      disabled={isDownloading}
                      onClick={() => handleDownloadInputs(type.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          />
        </GroupItem>
      </Form>
    </PopupWrapper>
  );
};

export default EditForm;
